import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, SafeAreaView, TouchableOpacity, ScrollView, Text, useWindowDimensions } from 'react-native';
import { Card } from 'react-native-paper';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../../components/CustomInput';
import CustomDropDown from '../../../../components/CustomDropDown/CustomDropdown';
import CustomButton from '../../../../components/CustomButton';
import { useCompany } from '../../../../hooks/useCompany';
import { useTranslation } from 'react-i18next';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { useNavigation } from '@react-navigation/native';
import { showMessage } from 'react-native-flash-message';
import Swal from 'sweetalert2';
import { createCommonStyles } from '../../../../styles/commonStyles';

const HRAdminWorkflowSteps = ({ route, onBack, description: propDescription }) => {
    const { workflowId, companyCode, categoryId } = route.params;
    const [description, setDescription] = useState(propDescription);

    useEffect(() => {
        setDescription(propDescription);
    }, [propDescription]);

    const [approverList, setApproverList] = useState([]);
    const [specificApprovers, setSpecificApprovers] = useState([]);
    const operators = [
        { label: '(', value: '(' },
        { label: ')', value: ')' },
        { label: 'AND', value: 'AND' },
        { label: 'OR', value: 'OR' },
    ];

    // State for selected values
    const [stepsResponse, setStepsResponse] = useState([]);
    const [step, setStep] = useState('1');
    const [stepsData, setStepsData] = useState({});
    const [stepOptions, setStepOptions] = useState([]);
    const [noOfApprovals, setNoOfApprovals] = useState('');
    const [selectedApprover, setSelectedApprover] = useState(null);
    const [selectedSpecificApprover, setSelectedSpecificApprover] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [approvalCriteria, setApprovalCriteria] = useState('');
    const [inputHeight, setInputHeight] = useState(0);
    const { hasWorkflowSteps, getWorkflowSteps, getGeneralApprovers, getSpecificApprovers, saveWorkflowStep,
        deleteWorkflowStep, deleteAllWorkflowSteps, validateAndParseApprovalExpression } = useCompany();
    const [hasSteps, setHasSteps] = useState(false);
    const { t } = useTranslation();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors);
    const { width } = useWindowDimensions();
    const maxHeight = width <= 1366 ? 200 : null;
    const navigation = useNavigation();
    const isHRCategory = categoryId?.toLowerCase() === 'hr' || categoryId?.toLowerCase() === 'hrchanges';
    const [criteriaElements, setCriteriaElements] = useState([]);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const isInitialCriteriaLoad = useRef(true);
    const isLoadingStepData = useRef(true);
    const sanitizeExpression = (expr) => {
        return expr.replace(/#{2,}/g, '#');
    };

    const showWarningPopup = async () => {
        return Swal.fire({
            title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:Criteriaexpressionhassyntaxerror"
            )}</text>`,
            icon: "warning",
            reverseButtons: true,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:cancel"
            )}</text>`,
            cancelButtonColor: "#d3d3d3",
        });
    };

    const handleGoBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigation.goBack();
        }
    };

    useEffect(() => {
        const fetchApprovers = async () => {
            try {
                if (categoryId) {
                    const generalApprovers = await getGeneralApprovers(categoryId);
                    if (generalApprovers && Array.isArray(generalApprovers)) {
                        const generalApproverOptions = generalApprovers.map(approver => ({
                            label: approver.displayMember,
                            value: approver.valueMember.replace(/#/g, ''),
                        }));
                        setApproverList(generalApproverOptions);
                    } else {
                        setApproverList([]);
                    }
                } else {
                    setApproverList([]);
                }

                if (companyCode) {
                    const specificApprovers = await getSpecificApprovers(companyCode);
                    if (specificApprovers && Array.isArray(specificApprovers)) {
                        const specificApproverOptions = specificApprovers.map(approver => ({
                            label: approver.displayMember,
                            value: approver.id,
                        }));
                        setSpecificApprovers(specificApproverOptions);
                    } else {
                        setSpecificApprovers([]);
                    }
                } else {
                    setSpecificApprovers([]);
                }
            } catch (error) {
                console.error('Error fetching approvers:', error);
                setApproverList([]);
                setSpecificApprovers([]);
            }
        };

        const fetchWorkflowData = async () => {
            try {
                const stepsExist = await hasWorkflowSteps(workflowId);
                setHasSteps(stepsExist);

                if (stepsExist) {
                    const stepsResponse = await getWorkflowSteps(companyCode, workflowId);
                    setStepsResponse(stepsResponse || []);

                    if (Array.isArray(stepsResponse) && stepsResponse.length > 0) {
                        const options = stepsResponse.map(step => ({
                            label: `Step ${step.step}`,
                            value: step.step.toString(),
                        }));
                        setStepOptions(options);

                        const firstStep = stepsResponse[0];
                        setStep(firstStep.step.toString());
                        setNoOfApprovals(firstStep.approvalsNo?.toString() || '');
                        setApprovalCriteria(firstStep.approvalExpr || '');
                    } else {
                        setStepOptions([]);
                        setStep(null);
                        setNoOfApprovals('');
                        setApprovalCriteria('');
                    }
                } else {
                    setStepsResponse([]);
                    setStepOptions([]);
                    setStep(null);
                    setNoOfApprovals('');
                    setApprovalCriteria('');
                }
            } catch (error) {
                console.error('Error loading workflow data:', error);
            } finally {
                isLoadingStepData.current = false;
            };
        }
        isLoadingStepData.current = true;
        fetchApprovers();
        fetchWorkflowData();
    }, [workflowId, companyCode, categoryId]);

    useEffect(() => {
        if (!step) return;
        const selectedStep = stepsResponse.find(s => s.step.toString() === step);
        if (selectedStep) {
            setNoOfApprovals(selectedStep.approvalsNo?.toString() || '');
            const exprWithNames = convertIdsToNamesInExpr(selectedStep.approvalExpr || '', approverList, specificApprovers);
            setApprovalCriteria(exprWithNames);
            setSelectedApprover(selectedStep.selectedApprover || null);
            setSelectedSpecificApprover(selectedStep.selectedSpecificApprover || null);
            setSelectedOperator(selectedStep.selectedOperator || null);
        } else {
            setNoOfApprovals('');
            setApprovalCriteria('');
            setSelectedApprover(null);
            setSelectedSpecificApprover(null);
            setSelectedOperator(null);
        }
    }, [step, stepsResponse, approverList, specificApprovers]);


    useEffect(() => {
        if (stepsResponse.length > 0) {
            const initialData = {};
            stepsResponse.forEach(s => {
                initialData[s.step.toString()] = {
                    noOfApprovals: s.approvalsNo?.toString() || '',
                    approvalCriteria: s.approvalExpr || '',
                    selectedApprover: s.selectedApprover || null,
                    selectedSpecificApprover: s.selectedSpecificApprover || null,
                    selectedOperator: s.selectedOperator || null,
                };
            });
            setStepsData(initialData);
        } else {
            setStepsData({});
        }
    }, [stepsResponse]);

    useEffect(() => {
        const loadStepCriteria = async () => {
            if (!step) {
                setCriteriaElements([]);
                return;
            }
            if (!approverList.length && !specificApprovers.length) {
                return;
            }
            const selectedStep = stepsResponse.find(s => s.step.toString() === step);
            if (!selectedStep || !selectedStep.approvalExpr) {
                setCriteriaElements([]);
                return;
            }

            // Convert IDs to names first
            const exprWithNames = convertIdsToNamesInExpr(selectedStep.approvalExpr, approverList, specificApprovers);

            // Parsing function to split approvalExpr into approvers and operators
            const parseApprovalExpression = (expr) => {
                const mappedElements = [];
                const regex = /(#.*?#|\bAND\b|\bOR\b|\(|\))/gi;
                let lastIndex = 0;
                let match;
                while ((match = regex.exec(expr)) !== null) {
                    if (match.index > lastIndex) {
                        const approverName = expr.substring(lastIndex, match.index).trim();
                        if (approverName) {
                            mappedElements.push({ type: 'approver', value: approverName, label: approverName });
                        }
                    }
                    const token = match[0];
                    if (token.toUpperCase() === 'AND' || token.toUpperCase() === 'OR' || token === '(' || token === ')') {
                        mappedElements.push({ type: 'operator', value: token.toUpperCase() });
                    } else {
                        // After conversion, ideally no IDs wrapped in # remain, but keep fallback
                        mappedElements.push({ type: 'approver', value: token, label: token });
                    }
                    lastIndex = regex.lastIndex;
                }
                if (lastIndex < expr.length) {
                    const remaining = expr.substring(lastIndex).trim();
                    if (remaining) {
                        mappedElements.push({ type: 'approver', value: remaining, label: remaining });
                    }
                }
                return mappedElements;
            };
            try {
                const mappedElements = parseApprovalExpression(exprWithNames);
                setCriteriaElements(mappedElements);
            } catch (error) {
                console.error('Error parsing approval expression on load:', error);
                setCriteriaElements([]);
            }
        };
        loadStepCriteria();
        isInitialCriteriaLoad.current = false;
    }, [step, stepsResponse, approverList, specificApprovers]);


    // Whenever criteriaElements changes, update approvalCriteria string
    useEffect(() => {
        const expr = criteriaElements.map(el => {
            if (el.type === 'approver') return el.label || el.value;
            else return el.value;
        }).join(' ');
        setApprovalCriteria(expr);
    }, [criteriaElements]);

    useEffect(() => {
        setUnsavedChanges(isStepDataChanged());
    }, [approvalCriteria, noOfApprovals, criteriaElements, stepsData, stepsResponse, step]);


    // When user selects an operator, add it to criteriaElements
    const addOperatorToCriteria = (operator) => {
        setCriteriaElements(prev => [...prev, { type: 'operator', value: operator }]);
    };

    // When user selects an approver, add it similarly
    const addApproverToCriteria = (approver) => {
        setCriteriaElements(prev => {
            const exists = prev.some(el => el.type === 'approver' && el.value === `#${approver.id}#`);
            if (exists) return prev;
            return [...prev, { type: 'approver', value: `#${approver.id}#`, label: approver.label }];
        });
    };

    const onNoOfApprovalsChange = (val) => {
        let numericVal = val.replace(/[^0-9]/g, '');
        if (numericVal.length > 2) {
            numericVal = numericVal.slice(0, 2);
        }
        setNoOfApprovals(numericVal);
    };


    const convertIdsToNamesInExpr = (expr, approverList, specificApprovers) => {
        if (!expr) return '';
        const idToNameMap = {};
        [...approverList, ...specificApprovers].forEach(approver => {
            idToNameMap[approver.value] = approver.label;
        });
        // Regex to find IDs wrapped in #
        return expr.replace(/#(.*?)#/g, (match, id) => {
            const name = idToNameMap[id];
            return name ? name : match;
        });
    };

    const normalizeWhitespace = (str) => str.trim().replace(/\s+/g, ' ');
    const convertNamesToIdsInExpr = (expr, approverList, specificApprovers) => {
        if (!expr) return '';

        // Build map: normalized name (lowercase) -> id
        const nameToIdMap = {};
        [...approverList, ...specificApprovers].forEach(approver => {
            const normalizedLabel = normalizeWhitespace(approver.label).toLowerCase();
            nameToIdMap[normalizedLabel] = approver.value;
        });

        // Split expression by operators and parentheses, preserving them
        const pattern = /(\bAND\b|\bOR\b|\(|\))/gi;
        const tokens = expr.split(pattern).filter(t => t.trim() !== '');

        const convertedTokens = tokens.map(token => {
            const trimmed = token.trim();
            // If operator or parentheses, leave as is
            if (/^(AND|OR|\(|\))$/i.test(trimmed)) {
                return trimmed.toUpperCase();
            }
            // If already an ID (enclosed in #), leave as is
            if (trimmed.startsWith('#') && trimmed.endsWith('#')) {
                return trimmed;
            }
            // Normalize whitespace in token before lookup
            const normalizedToken = normalizeWhitespace(trimmed).toLowerCase();
            // Lookup normalized name in map
            const id = nameToIdMap[normalizedToken];
            if (id) {
                return `#${id}#`;
            }
            return trimmed;
        });
        return convertedTokens.join(' ');
    };

    const handleStepChange = async (newStep) => {
        if (!newStep || newStep === step) return;

        if (unsavedChanges) {
            const confirmed = await Swal.fire({
                title: 'You have unsaved changes. Save before switching?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Save',
                cancelButtonText: 'Discard',
            });
            if (confirmed.isConfirmed) {
                await handleSaveStep();
            }
        }
        isLoadingStepData.current = true;

        // Save current step data into stepsData state
        setStepsData(prev => ({
            ...prev,
            [step]: {
                noOfApprovals,
                approvalCriteria,
                selectedApprover,
                selectedSpecificApprover,
                selectedOperator,
                criteriaElements,
            }
        }));

        // Load new step data from stepsData or fallback to stepsResponse
        const newStepData = stepsData[newStep] || stepsResponse.find(s => s.step.toString() === newStep) || {
            noOfApprovals: '',
            approvalCriteria: '',
            selectedApprover: null,
            selectedSpecificApprover: null,
            selectedOperator: null,
            criteriaElements: [],
        };

        setNoOfApprovals(newStepData.noOfApprovals?.toString() || '');
        setApprovalCriteria(newStepData.approvalCriteria || newStepData.approvalExpr || '');
        setSelectedApprover(newStepData.selectedApprover || null);
        setSelectedSpecificApprover(newStepData.selectedSpecificApprover || null);
        setSelectedOperator(newStepData.selectedOperator || null);
        setCriteriaElements(newStepData.criteriaElements || []);
        setStep(newStep);
        isLoadingStepData.current = false;
    };

    const normalize = str => (str || '').trim().replace(/\s+/g, ' ');
    const isStepDataChanged = () => {
        if (!step) return false;
        const saved = stepsData[step] || stepsResponse.find(s => s.step.toString() === step) || {};
        const savedExprNames = normalize(convertIdsToNamesInExpr(saved.approvalCriteria || saved.approvalExpr || '', approverList, specificApprovers));
        const currentExpr = normalize(approvalCriteria);
        const savedApprovalsNo = (saved.noOfApprovals?.toString() || '').trim();
        const currentApprovalsNo = noOfApprovals.trim();
        return (
            savedApprovalsNo !== currentApprovalsNo ||
            savedExprNames !== currentExpr
        );
    };

    useEffect(() => {
        if (isLoadingStepData.current) {
            return;
        }
        const changed = isStepDataChanged();
        setUnsavedChanges(changed);
    }, [approvalCriteria, noOfApprovals, criteriaElements, stepsData, stepsResponse, step]);

    const handleAddNewStep = async () => {
        if (unsavedChanges) {
            const confirmed = await Swal.fire({
                title: 'You have unsaved changes. Save before adding a new step?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Save',
                cancelButtonText: 'Discard',
                reverseButtons: true,
            });

            if (confirmed.isConfirmed) {
                // Save current step first
                await handleSaveStep();
            } else if (!confirmed.isDismissed && !confirmed.isConfirmed) {
                // User cancelled the popup, do nothing
                return;
            }
            // If user discarded changes, continue to add new step
        }
        const existingSteps = stepsResponse.map(s => parseInt(s.step, 10));
        const maxStep = existingSteps.length > 0 ? Math.max(...existingSteps) : 0;
        const newStepNumber = maxStep + 1;

        const newStep = {
            step: newStepNumber,
            approvalsNo: '',
            approvalExpr: '',
        };

        const updatedStepsResponse = [...stepsResponse, newStep];
        setStepsResponse(updatedStepsResponse);

        const updatedStepOptions = updatedStepsResponse.map(s => ({
            label: `Step ${s.step}`,
            value: s.step.toString(),
        }));
        setStepOptions(updatedStepOptions);

        setStepsData(prev => ({
            ...prev,
            [newStepNumber]: {
                noOfApprovals: '',
                approvalCriteria: '',
                selectedApprover: null,
                selectedSpecificApprover: null,
                selectedOperator: null,
            }
        }));

        setStep(newStepNumber.toString());
        setUnsavedChanges(false);
    };

    const validateParentheses = (expr) => {
        let balance = 0;
        for (let char of expr) {
            if (char === '(') balance++;
            else if (char === ')') balance--;
            if (balance < 0) return false;
        }
        return balance === 0;
    };



    const handleSaveStep = async () => {
        if (!step) {
            showMessage({ message: t('common:pleaseSelectStepToSave'), type: 'warning' });
            return;
        }
        if (!noOfApprovals || noOfApprovals.trim() === '') {
            showMessage({ message: t('common:noOfApprovalsRequired'), type: 'warning' });
            return;
        }
        if (criteriaElements.length === 0) {
            showMessage({ message: 'Approval criteria cannot be empty.', type: 'warning' });
            return;
        }
        let approvalExpr = convertNamesToIdsInExpr(approvalCriteria, approverList, specificApprovers)

        // Sanitize expression to fix double hashes
        approvalExpr = sanitizeExpression(approvalExpr);

        if (!validateParentheses(approvalExpr)) {
            showMessage({ message: 'Parentheses are not balanced.', type: 'danger' });
            return;
        }
        const validation = await validateAndParseApprovalExpression(approvalExpr, companyCode);

        if (!validation || validation.isValid !== true) {
            if (validation && validation.error) {
                await showWarningPopup();
            } else {
                showMessage({ message: 'Approval expression validation failed.', type: 'danger' });
            }
            return;
        }

        const stepData = {
            StepNo: step,
            ApprovalsNo: noOfApprovals ? parseInt(noOfApprovals, 10) : null,
            ApprovalExpr: approvalExpr,
        };
        try {
            await saveWorkflowStep(companyCode, workflowId, stepData);
            showMessage({
                message: t('common:workflowStepSavedSuccessfully'),
                type: 'success'
            });
            const updatedSteps = await getWorkflowSteps(companyCode, workflowId);
            setStepsResponse(updatedSteps || []);
        } catch (error) {
            console.error('Error saving workflow step:', error);
            showMessage({
                message: t('common:failedToSaveWorkflowStep'),
                type: 'danger'
            });
        }
    }


    const handleDeleteStep = async () => {
        if (!step) {
            showMessage({
                message: t('common:pleaseSelectStepToDelete'),
                type: 'warning',
            });
            return;
        }

        // Show confirmation popup before deleting
        const result = await Swal.fire({
            title: t('common:areYouSureDeleteStep'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common:confirm'),
            cancelButtonText: t('common:cancel'),
            reverseButtons: true,
        });

        if (result.isConfirmed) {
            try {
                const deleteResult = await deleteWorkflowStep(companyCode, workflowId, step);
                if (deleteResult.success) {
                    showMessage({
                        message: t('common:workflowStepDeletedSuccessfully'),
                        type: 'success',
                    });
                    const updatedSteps = await getWorkflowSteps(companyCode, workflowId);
                    setStepsResponse(updatedSteps || []);
                    if (Array.isArray(updatedSteps) && updatedSteps.length > 0) {
                        const options = updatedSteps.map(s => ({
                            label: `Step ${s.step}`,
                            value: s.step.toString(),
                        }));
                        setStepOptions(options);
                        setStep(updatedSteps[0].step.toString());
                    } else {
                        setStepOptions([]);
                        setStep(null);
                        setHasSteps(false);
                    }
                    setNoOfApprovals('');
                    setApprovalCriteria('');
                    setSelectedApprover(null);
                    setSelectedSpecificApprover(null);
                    setSelectedOperator(null);
                } else {
                    showMessage({
                        message: t('common:failedToDeleteWorkflowStep'),
                        type: 'danger',
                    });
                }
            } catch (error) {
                console.error('Error deleting workflow step:', error);
                showMessage({
                    message: t('common:errorDeletingWorkflowStep'),
                    type: 'danger',
                });
            }
        }
    };

    const handleDeleteAllSteps = async () => {

        const result = await Swal.fire({
            title: t('common:areYouSureDeleteAllSteps'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common:confirm'),
            cancelButtonText: t('common:cancel'),
            reverseButtons: true,
        });

        if (result.isConfirmed) {
            try {
                const deleteResult = await deleteAllWorkflowSteps(companyCode, workflowId);
                if (deleteResult.success) {
                    showMessage({
                        message: t('common:allWorkflowStepsDeletedSuccessfully'),
                        type: 'success',
                    });
                    setStepsResponse([]);
                    setStepOptions([]);
                    setStep(null);
                    setNoOfApprovals('');
                    setApprovalCriteria('');
                    setSelectedApprover(null);
                    setSelectedSpecificApprover(null);
                    setSelectedOperator(null);
                    setApproverList([]);
                    setSpecificApprovers([]);
                    setHasSteps(false);
                } else {
                    throw new Error('Failed to delete all workflow steps');
                }
            } catch (error) {
                console.error('Error deleting all workflow steps:', error);
                showMessage({
                    message: t('common:failedToDeleteAllWorkflowSteps'),
                    type: 'danger',
                });
            }
        }
    };

    return (
        <SafeAreaView style={styles.mainView}>
            <View style={styles.container}>
                <View style={styles.headerRow}>
                    <CustomLabel
                        value={description}
                        type="TITLE"
                        style={styles.workflowNameText}
                    />
                    <TouchableOpacity onPress={handleGoBack} style={styles.backButton}>
                        <Ionicons name="arrow-back" size={30} color="black" />
                    </TouchableOpacity>
                </View>
                <SafeAreaView style={{ flex: 1 }}>
                    <View style={[styles.buttons, styles.functionalityButtons]}>
                        <CustomButton
                            text={t('common:save')}
                            onPress={handleSaveStep}
                            type='BUTTON_SAVE'
                            isGradient={true}
                            textTop="0"
                            sz={15}
                        />
                        <CustomButton
                            text={t('common:newStep')}
                            onPress={handleAddNewStep}
                            type='BUTTON_ADD'
                            textTop="0"
                            sz={15}
                        />
                        <CustomButton
                            text={t('common:deleteStep')}
                            onPress={handleDeleteStep}
                            type="BUTTON_CANCEL"
                            textTop="0"
                            sz={15}
                            disabled={!hasSteps}
                        />
                        <CustomButton
                            text={t('common:deleteAllSteps')}
                            onPress={handleDeleteAllSteps}
                            type="BUTTON_CANCEL"
                            textTop="0"
                            sz={15}
                            disabled={!hasSteps}
                        />
                    </View>
                    <SafeAreaView style={common.divider} />
                    <ScrollView scrollEnabled={maxHeight === 200}
                        contentContainerStyle={{
                            maxHeight: maxHeight || 'auto',
                            paddingBottom: 20,
                        }}>
                        <Card style={common.cardstyle}>
                            <Card.Content>
                                <View style={styles.rowContainer}>
                                    <View style={styles.item}>
                                        <CustomLabel value={t("common:step")} type="SUBTITLE" />
                                        <View style={styles.dropdown}>
                                            <CustomDropDown
                                                value={step}
                                                defValue={step}
                                                setValue={handleStepChange}
                                                options={stepOptions}
                                                type="NEW_COMPANY"
                                                disabled={false}
                                            />
                                        </View>
                                    </View>
                                    <View style={styles.item}>
                                        <CustomLabel value={t("common:noofApprovals")} type="SUBTITLE" />
                                        <View style={styles.inputContainer}>
                                            <CustomInput
                                                value={noOfApprovals}
                                                defValue={noOfApprovals}
                                                setValue={onNoOfApprovalsChange}
                                                editable={true}
                                                placeholder="Enter number of approvals"
                                                keyboardType="numeric"
                                                type="MAININPUT"
                                            />
                                        </View>
                                    </View>
                                    <View style={styles.item}>
                                        <CustomLabel value={t("common:approverlist")} type="SUBTITLE" />
                                        <View style={styles.dropdown}>
                                            <CustomDropDown
                                                multipleSelect={false}
                                                value={selectedApprover}
                                                setValue={(val) => {
                                                    const approver = approverList.find(a => a.value === val);
                                                    if (approver) {
                                                        addApproverToCriteria({ id: val, label: approver.label });
                                                        setSelectedApprover(val);
                                                    }
                                                }}
                                                options={approverList}
                                                type="NEW_COMPANY"
                                                disabled={!step || isHRCategory}
                                                style={isHRCategory ? { backgroundColor: '#f7f0f0' } : {}}
                                            />
                                        </View>
                                    </View>
                                </View>
                                <View style={styles.secRowContainer}>
                                    <View style={styles.item}>
                                        <CustomLabel value={t("common:specificapprovers")} type="SUBTITLE" />
                                        <View style={styles.dropdown}>
                                            <CustomDropDown
                                                key={JSON.stringify(selectedSpecificApprover)}
                                                multipleSelect={true}
                                                value={selectedSpecificApprover}
                                                setValue={(vals) => {
                                                    setSelectedSpecificApprover(vals);
                                                    if (Array.isArray(vals)) {
                                                        vals.forEach(val => {
                                                            const approver = specificApprovers.find(a => a.value === val);
                                                            if (approver) {
                                                                const cleanId = val.replace(/#/g, '');
                                                                addApproverToCriteria({ id: cleanId, label: approver.label });
                                                            }
                                                        });
                                                    } else if (vals) {
                                                        const approver = specificApprovers.find(a => a.value === vals);
                                                        if (approver) {
                                                            const cleanId = vals.replace(/#/g, '');
                                                            addApproverToCriteria({ id: cleanId, label: approver.label });
                                                        }
                                                    }
                                                }}
                                                options={specificApprovers}
                                                type="NEW_COMPANY"
                                                disabled={false}
                                            />
                                        </View>
                                    </View>
                                    <View style={styles.item}>
                                        <CustomLabel value={t("common:operators")} type="SUBTITLE" />
                                        <View style={styles.dropdown}>
                                            <CustomDropDown
                                                multipleSelect={false}
                                                value={selectedOperator}
                                                setValue={(val) => {
                                                    addOperatorToCriteria(val);
                                                    setSelectedOperator(val);
                                                }}
                                                options={operators}
                                                type="NEW_COMPANY"
                                                disabled={false}
                                            />
                                        </View>
                                    </View>
                                </View>

                                {/* Approval criteria input field */}
                                <View style={styles.row2}>
                                    <View style={styles.item}>
                                        <CustomLabel value={t("common:approvalcriteria")} type="SUBTITLE" />
                                        <View>
                                            <CustomInput
                                                value={approvalCriteria}
                                                onChangeText={async (text) => {
                                                    setApprovalCriteria(text);
                                                    // Parse updated text back into criteria elements
                                                    const elements = await parseApprovalExpression(text);
                                                    // Map approver IDs to labels for display
                                                    const mappedElements = elements.map(el => {
                                                        if (el.type === 'approver') {
                                                            const approverId = el.value.replace(/#/g, '');
                                                            const approver = [...approverList, ...specificApprovers].find(a => a.value === approverId);
                                                            return {
                                                                ...el,
                                                                label: approver ? approver.label : el.value,
                                                            };
                                                        }
                                                        return el;
                                                    });
                                                    setCriteriaElements(mappedElements);
                                                }}
                                                editable={true}
                                                type="MAININPUT"
                                                multiline={true}
                                                numberOfLines={Math.min(Math.max(1, Math.floor(inputHeight / 20)), 4)}
                                                style={{ height: Math.min(Math.max(40, inputHeight), 80) }}
                                                onContentSizeChange={(event) => {
                                                    const height = event.nativeEvent.contentSize.height;
                                                    setInputHeight(height);
                                                }}
                                            />
                                            <ScrollView horizontal style={{ marginVertical: 10 }}>
                                                {criteriaElements.map((el, index) => (
                                                    <TouchableOpacity
                                                        key={index}
                                                        onPress={() => {
                                                            // Remove element on tap
                                                            setCriteriaElements(prev => prev.filter((_, i) => i !== index));
                                                        }}
                                                        style={{
                                                            paddingHorizontal: 10,
                                                            paddingVertical: 5,
                                                            marginRight: 5,
                                                            backgroundColor: el.type === 'operator' ? '#86BC25' : '#d0d0d0',
                                                            borderRadius: 15,
                                                        }}
                                                    >
                                                        <Text style={{ color: el.type === 'operator' ? 'white' : 'black' }}>
                                                            {el.label || el.value}
                                                        </Text>
                                                    </TouchableOpacity>
                                                ))}
                                            </ScrollView>
                                        </View>
                                    </View>
                                </View>
                            </Card.Content>
                        </Card>
                    </ScrollView>
                </SafeAreaView>
            </View>
        </SafeAreaView>
    );
};

const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
        marginHorizontal: 10,
    },
    container: {
        // flex: 1,
        marginBottom: 10,
    },
    dropdown: {
        width: '95%',
    },
    inputContainer: {
        padding: 0,
        width: '95%',
    },
    rowContainer: {
        // flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: 'space-between',
    },
    secRowContainer: {
        // flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: 'space-between',
    },
    item: {
        flex: 1,
        justifyContent: "center",
        marginHorizontal: 5,
    },
    backButton: {
        alignSelf: 'flex-end',
        margin: 5,
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
    },
    workflowNameText: {
        fontWeight: 'bold',
        fontSize: 20,
        color: colors.text,
    },
    headerRow: {
        height: '5%',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        marginBottom: 10,
        justifyContent: 'space-between',
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
        marginRight: 15,
    },
    disabledDropdown: {
        backgroundColor: '#f7f0f0',
    },
    row2: {
        // flex: 1,
        flexDirection: "row",
        alignItems: "center",
        margin: 10
    },
});

export default HRAdminWorkflowSteps;